import { useCallback, useMemo, useReducer } from "react";
import {
  bulkMeterUploadReducer,
  initialBulkMeterUploadState,
} from "@context/bulkMeterUploadReducer";
import type { Level1WRSRef } from "@context/bulkMeterUploadReducer";
import {
  buildTrialRunRow,
  type ManualIntervention,
  type MeterUploadRow,
  type MeterUploadRowData,
  type TrialRunDisplayRow,
} from "@utils/bulkMeterUpload/buildTrialRunRow";
import { selectRowsForLedger } from "@utils/bulkMeterUpload/selectRowsForLedger";
import {
  computeParentStatus,
  type MeterUploadStatusValue,
} from "@utils/bulkMeterUpload/computeParentStatus";
import { getMeterUploadDraft } from "@services/bulkMeterUpload";
import { useCreateMeterUploadDraft } from "@hooks/mutation/useCreateMeterUploadDraft";
import { useCreateMeterUploadRow } from "@hooks/mutation/useCreateMeterUploadRow";
import { useUpdateMeterUploadRow } from "@hooks/mutation/useUpdateMeterUploadRow";
import { useDeleteMeterUploadRow } from "@hooks/mutation/useDeleteMeterUploadRow";
import { useTrialRunMeterUpload } from "@hooks/mutation/useTrialRunMeterUpload";
import { useFinalizeMeterUpload } from "@hooks/mutation/useFinalizeMeterUpload";

export type UseBulkMeterUploadFlowOpts = {
  level1ResourceId: string;
  draftId?: string;
};

// The trial-run endpoint validates each row with accountNumber, extractionPointName
// and readAt as required, non-empty strings, and rejects the entire array request if
// any element is invalid. A freshly inserted blank inline row therefore can't be sent
// until those fields are filled — hold it back here so it never rejects the batch; it
// still renders, flagged, via buildTrialRunRow's empty-result fallback.
const isRowReadyForTrialRun = (rowData: MeterUploadRowData): boolean =>
  rowData.accountNumber.trim() !== "" &&
  rowData.extractionPointName.trim() !== "" &&
  rowData.readAt.trim() !== "";

export const useBulkMeterUploadFlow = ({
  level1ResourceId,
  draftId,
}: UseBulkMeterUploadFlowOpts) => {
  const [state, dispatch] = useReducer(
    bulkMeterUploadReducer,
    initialBulkMeterUploadState,
  );

  const createDraftMutation = useCreateMeterUploadDraft();
  const createRowMutation = useCreateMeterUploadRow();
  const updateRowMutation = useUpdateMeterUploadRow();
  const deleteRowMutation = useDeleteMeterUploadRow();
  const trialRunMutation = useTrialRunMeterUpload();
  const finalizeMutation = useFinalizeMeterUpload();

  const setRowsFromFile = useCallback(
    (rows: MeterUploadRow[], fileName: string) => {
      dispatch({ type: "rowsParsedFromFile", rows, fileName });
    },
    [],
  );

  const setLevel1WRS = useCallback((level1WRS: Level1WRSRef) => {
    dispatch({ type: "level1WRSSelected", level1WRS });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  const applyManualIntervention = useCallback(
    async (rowId: string, intervention: ManualIntervention) => {
      if (state.meterUploadId) {
        const existing = state.rows.find(r => r.id === rowId);
        if (!existing) return;
        await updateRowMutation.mutateAsync({
          meterUploadId: state.meterUploadId,
          rowId,
          input: {
            rowData: existing.rowData,
            manualIntervention: intervention,
          },
        });
      }
      dispatch({ type: "manualInterventionApplied", rowId, intervention });
    },
    [updateRowMutation, state.meterUploadId, state.rows],
  );

  const runTrialRun = useCallback(async () => {
    // Only rows with their required identity fields filled in are validated; blank /
    // partially-filled inline rows are held back (see isRowReadyForTrialRun) so they
    // can't reject the whole batch. Held-back rows render flagged via the fallback.
    const sendable = state.rows.filter(r => isRowReadyForTrialRun(r.rowData));
    const serverRows = await trialRunMutation.mutateAsync({
      level1ResourceId,
      bulkMeterReadings: sendable.map(r => r.rowData),
    });
    dispatch({
      type: "trialRunCompleted",
      rowIds: sendable.map(r => r.id),
      serverRows,
    });
  }, [trialRunMutation, level1ResourceId, state.rows]);

  const saveDraft = useCallback(async () => {
    if (state.meterUploadId) {
      return state.meterUploadId;
    }
    const draft = await createDraftMutation.mutateAsync({
      level1ResourceId,
      fileName: state.fileName,
      rows: state.rows.map(r => ({
        id: r.id,
        rowData: r.rowData,
        manualIntervention: r.manualIntervention,
      })),
    });
    dispatch({
      type: "draftHydrated",
      draft: {
        meterUploadId: draft.id,
        level1WRS: state.level1WRS ?? { id: level1ResourceId, name: "" },
        fileName: draft.fileName ?? "",
        rows: draft.rows.length ? draft.rows : state.rows,
      },
    });
    return draft.id;
  }, [
    createDraftMutation,
    level1ResourceId,
    state.meterUploadId,
    state.fileName,
    state.rows,
    state.level1WRS,
  ]);

  const hydrateFromServer = useCallback(async () => {
    if (!draftId) return;
    const draft = await getMeterUploadDraft(draftId);
    dispatch({
      type: "draftHydrated",
      draft: {
        meterUploadId: draft.id,
        level1WRS: {
          id: draft.level1ResourceId,
          name: draft.level1ResourceName ?? "",
        },
        fileName: draft.fileName ?? "",
        rows: draft.rows,
      },
    });
  }, [draftId]);

  const editRow = useCallback(
    async (rowId: string, patch: MeterUploadRowData) => {
      const existing = state.rows.find(r => r.id === rowId);
      if (!existing) return;
      if (state.meterUploadId) {
        const updated = await updateRowMutation.mutateAsync({
          meterUploadId: state.meterUploadId,
          rowId,
          input: {
            rowData: patch,
            manualIntervention: existing.manualIntervention,
          },
        });
        // Per-row fold-in: this closure's state.rows snapshot may be stale by the
        // time the PATCH resolves (another edit/add settled in between), so the
        // reducer merges by id against live state instead of taking a rebuilt array.
        dispatch({ type: "rowReplaced", rowId, row: updated });
      } else {
        dispatch({ type: "rowEdited", rowId, patch });
      }
    },
    [updateRowMutation, state.meterUploadId, state.rows],
  );

  const addRow = useCallback(
    async (row: MeterUploadRow) => {
      if (state.meterUploadId) {
        const created = await createRowMutation.mutateAsync({
          meterUploadId: state.meterUploadId,
          input: {
            id: row.id,
            rowData: row.rowData,
            manualIntervention: row.manualIntervention,
          },
        });
        dispatch({ type: "rowAdded", row: created });
      } else {
        dispatch({ type: "rowAdded", row });
      }
    },
    [createRowMutation, state.meterUploadId],
  );

  const deleteRow = useCallback(
    async (rowId: string) => {
      if (state.meterUploadId) {
        await deleteRowMutation.mutateAsync({
          meterUploadId: state.meterUploadId,
          rowId,
        });
      }
      dispatch({ type: "rowDeleted", rowId });
    },
    [deleteRowMutation, state.meterUploadId],
  );

  const ledger = useCallback(async (idArg?: string) => {
    // On a fresh upload the caller saves the draft first and passes the new id here:
    // state.meterUploadId is set via dispatch but isn't visible in this closure until
    // the next render, so prefer the explicit id over the (still-stale) state value.
    const id = idArg ?? state.meterUploadId ?? draftId;
    if (!id) return;
    const displayRows = state.rows.map(row =>
      buildTrialRunRow({ row, trialResult: state.trialServerRows[row.id] }),
    );
    const rowIds = selectRowsForLedger(displayRows);
    // Fire-and-forget: the server returns 202 and ledgers detached. We do not apply
    // results client-side; the operator refreshes the upload to see rows settle.
    await finalizeMutation.mutateAsync({ id, rowIds });
  }, [finalizeMutation, state.meterUploadId, draftId, state.rows, state.trialServerRows]);

  const parentStatus: MeterUploadStatusValue = useMemo(
    () => computeParentStatus(state.rows),
    [state.rows],
  );

  // Every row renders — including ones inserted inline that have no server result
  // yet — so a freshly added blank row appears immediately and is editable. Rows
  // without a result fall back to an empty trial result and render flagged.
  const displayRows: TrialRunDisplayRow[] = useMemo(
    () =>
      state.rows.map(r =>
        buildTrialRunRow({ row: r, trialResult: state.trialServerRows[r.id] }),
      ),
    [state.rows, state.trialServerRows],
  );

  return {
    rows: state.rows,
    meterUploadId: state.meterUploadId,
    workflowInstanceId: state.workflowInstanceId,
    fileName: state.fileName,
    // Fall back to the route's scheme when no scheme was explicitly selected:
    // SelectLevel1WRS only seeds level1WRS from react-router navigation state, so a
    // deep-link / refresh leaves it unset and the account / offtake lookups (gated on
    // level1WRS?.id) never fire. The flow is always scoped to a level1ResourceId.
    level1WRS:
      state.level1WRS ??
      (level1ResourceId ? { id: level1ResourceId, name: "" } : undefined),
    displayRows,
    trialServerRows: state.trialServerRows,
    isLedgering: finalizeMutation.isLoading,
    isTrialRunning: trialRunMutation.isLoading,
    setRowsFromFile,
    setLevel1WRS,
    reset,
    runTrialRun,
    applyManualIntervention,
    saveDraft,
    hydrateFromServer,
    addRow,
    editRow,
    deleteRow,
    ledger,
    parentStatus,
  };
};
